import { ComponentsCustomer } from "@omni/types/components/customer/customer.model";
import { head } from "@omni/utils/array.utils";
import { attempt } from "@omni/utils/function.utils";
import { parsePhoneNumber } from "libphonenumber-js";
import queryString from "query-string";
import { DefaultValues } from "react-hook-form";
import * as yup from "yup";

import { Category } from "@/components/ContactCustomerservice/contactCustomerservice.types";
import { OptionalAttachmentsSchema } from "@/components/Form/RHF/FormFields/MultiUploadField/multiUploadField.schema";
import { OptionalPhoneSchema } from "@/components/Form/RHF/FormFields/PhoneField/phoneField.schema";
import { StoreSchema } from "@/components/Form/RHF/FormFields/StoreField/storeField.schema";
import { PhonenumberPrefix } from "@/redux/modules/api/data/phonenumberPrefixes/phonenumberPrefixes.types";
import * as RHFBonusCardValidationRules from "@/utils/validation/schemas/bonusCard.RHF";
import * as RHFGeneralValidationRules from "@/utils/validation/schemas/general.RHF";
import * as RHFUserValidationRules from "@/utils/validation/schemas/user.RHF";

export const MAX_FILE_UPLOADS = 5;
export const MAX_UPLOAD_SIZE_MB = 4;
export const ACCEPTED_FILE_TYPES = [
    { mime: "image/jpeg", extensions: [".jpg", ".jpeg"] },
    { mime: "image/png", extensions: [".png"] }
];

export const ContactCustomerserviceValidationSchema = yup
    .object({
        category: RHFGeneralValidationRules.category,
        orderId: RHFGeneralValidationRules.optionalOrderId,
        firstName: RHFUserValidationRules.optionalFirstName,
        lastName: RHFUserValidationRules.lastName,
        email: RHFUserValidationRules.emailWithNB,
        bonusCardNumber: RHFBonusCardValidationRules.optionalBonusCard,
        storeId: RHFGeneralValidationRules.storeId,
        message: RHFGeneralValidationRules.message(1500)
    })
    .concat(StoreSchema)
    .concat(OptionalPhoneSchema)
    .concat(
        OptionalAttachmentsSchema(
            MAX_FILE_UPLOADS,
            MAX_UPLOAD_SIZE_MB,
            ACCEPTED_FILE_TYPES
        )
    );

export type ContactCustomerserviceFormValues = yup.InferType<
    typeof ContactCustomerserviceValidationSchema
>;

const getStoreId = (customer?: ComponentsCustomer | null) =>
    customer?.favouriteStoreId ??
    attempt<string | undefined>(
        () => queryString.parse(location.search).storeId?.toString(),
        undefined
    ) ??
    undefined;

export const getInitialFormValues = (
    customer?: ComponentsCustomer | null,
    categories?: Array<Category>
): DefaultValues<ContactCustomerserviceFormValues> => ({
    category: head(categories ?? [])?.code ?? emptyFormValues.category,
    firstName: customer?.firstName ?? emptyFormValues.firstName,
    lastName: customer?.lastName ?? emptyFormValues.lastName,
    email: customer?.email ?? emptyFormValues.email,
    storeId: getStoreId(customer) ?? emptyFormValues.storeId,
    phonePrefix: customer?.phonePrefix ?? emptyFormValues.phonePrefix,
    phone: customer?.phone ?? emptyFormValues.phone,
    bonusCardNumber:
        customer?.bonusCardNumber ?? emptyFormValues.bonusCardNumber,
    message: emptyFormValues.message,
    attachments: emptyFormValues.attachments,
    orderId: emptyFormValues.orderId || ""
});

export const getPhone = (
    phonenumberPrefixes: PhonenumberPrefix[],
    prefix?: string,
    phone?: string | null
) => {
    let newPhone;
    if (prefix && phone) {
        const phonePrefix = phonenumberPrefixes.find(
            (p) => p.prefix === prefix
        );
        if (phonePrefix) {
            try {
                newPhone = parsePhoneNumber(
                    `${prefix}${phone}`,
                    phonePrefix.country.isoCode
                );
            } catch (_) {
                newPhone = undefined;
            }
        }
    }
    return newPhone?.isValid() ? newPhone.number : undefined;
};

export const getCategoryByCode =
    (categories: Array<Category>) => (code?: string) =>
        categories.find((c) => c.code === code)?.name;

export const categoriesToFormOptions = (categories: Array<Category>) =>
    categories.map(({ name, code }) => ({
        label: name,
        value: code
    }));

export const emptyFormValues: DefaultValues<ContactCustomerserviceFormValues> =
    {
        lastName: "",
        phonePrefix: "+32",
        category: "",
        email: "",
        message: "",
        storeId: "",
        attachments: [],
        firstName: "",
        phone: "",
        orderId: "",
        bonusCardNumber: ""
    };
